/**
 * Leaflet Map
 * Station map with markers and region switching
 * Lazy-loads Leaflet library on first use for better TBT
 */

import { AppState, setCurrentStation, getCurrentRegionId, getCurrentAreaId, isSpotMode } from './state.js';
import { updateUrl } from './router.js';
import { updateLocationButtonText, updatePageTitle } from './ui-selectors.js';
import { getSpotByStationId } from './config-loader.js';
import { analytics } from './analytics.js';
import { logger } from './logger.js';

// Leaflet lazy loading state
let leafletLoaded = false;
let leafletPromise = null;

/**
 * Lazy-load Leaflet library
 */
async function loadLeaflet() {
    if (leafletLoaded) return;
    if (leafletPromise) return leafletPromise;

    leafletPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        script.onload = () => {
            leafletLoaded = true;
            resolve();
        };
        script.onerror = () => {
            reject(new Error('Failed to load Leaflet'));
        };
        document.head.appendChild(script);
    });

    return leafletPromise;
}

// Global map and marker references
let map = null;
let stationMarkers = {};
let darkTileLayer = null;
let lightTileLayer = null;
let currentAreaId = null;

// Import refreshAllData dynamically to avoid circular dependency
let refreshAllDataFn = null;

/**
 * Set the refresh function (called from app.js)
 */
export function setRefreshFunction(fn) {
    refreshAllDataFn = fn;
}

/**
 * Initialize the Leaflet map for a specific area
 * Lazy-loads Leaflet on first call
 * @param {Object} area - Area configuration object (with mapCenter, mapZoom, stations)
 */
export async function initMap(area = null) {
    // Lazy-load Leaflet library if not already loaded
    await loadLeaflet();

    // Determine initial theme
    const isLightMode = document.documentElement.classList.contains('light-mode');

    // Use area config or defaults for San Diego
    const mapCenter = area?.mapCenter || { lat: 32.79, lon: -117.22 };
    const mapZoom = area?.mapZoom || 11;

    map = L.map('map', {
        center: [mapCenter.lat, mapCenter.lon],
        zoom: mapZoom,
        scrollWheelZoom: true
    });

    // Create both tile layers
    darkTileLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19
    });

    lightTileLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19
    });

    // Add appropriate tile layer based on current theme
    if (isLightMode) {
        lightTileLayer.addTo(map);
    } else {
        darkTileLayer.addTo(map);
    }

    // Add station markers if area provided
    if (area) {
        currentAreaId = area.id;
        addStationMarkers(area.stations, AppState.currentStationId);
    }
}

/**
 * Update map for a new area
 * @param {Object} area - New area configuration (with mapCenter, mapZoom, stations)
 * @param {string} stationId - Selected station ID
 */
export async function updateMapForRegion(area, stationId) {
    if (!map) {
        await initMap(area);
        return;
    }

    if (!area) {
        logger.warn('updateMapForRegion called without area');
        return;
    }

    const isNewArea = currentAreaId !== area.id;

    if (isNewArea) {
        // Clear existing markers
        clearStationMarkers();

        // Animate to new area
        map.flyTo(
            [area.mapCenter.lat, area.mapCenter.lon],
            area.mapZoom,
            { duration: 1.5 }
        );

        currentAreaId = area.id;
    }

    // Add markers for area's stations
    addStationMarkers(area.stations, stationId);
}

/**
 * Clear all station markers from the map
 */
function clearStationMarkers() {
    Object.values(stationMarkers).forEach(marker => {
        if (map) map.removeLayer(marker);
    });
    stationMarkers = {};
}

/**
 * Add station markers to the map
 * @param {Object} stations - Stations object from region config
 * @param {string} selectedStationId - Currently selected station ID
 */
function addStationMarkers(stations, selectedStationId) {
    // Clear existing markers first
    clearStationMarkers();

    Object.entries(stations).forEach(([id, station]) => {
        const isSelected = id === selectedStationId;

        const icon = L.divIcon({
            className: `station-marker ${isSelected ? 'selected' : ''}`,
            html: '<div class="marker-pin"></div>',
            iconSize: [24, 24],
            iconAnchor: [12, 12]
        });

        const marker = L.marker([station.lat, station.lon], {
            icon: icon,
            title: station.name
        }).addTo(map);

        const localName = station.localName ? `<br><small>${station.localName}</small>` : '';
        marker.bindPopup(`
            <strong>${station.name}</strong>${localName}
            <button class="popup-btn" onclick="switchStation('${id}')">
                ${isSelected ? 'Selected' : 'Select'}
            </button>
        `, { closeButton: false, maxWidth: 200 });

        stationMarkers[id] = marker;
    });

    // Fit map bounds to show all stations with padding
    const stationCoords = Object.values(stations).map(s => [s.lat, s.lon]);
    if (stationCoords.length > 1) {
        map.fitBounds(stationCoords, { padding: [50, 50] });
    }
}

/**
 * Update map tile layer when theme changes
 */
export function updateMapTheme(isLightMode) {
    if (!map) return;

    if (isLightMode) {
        if (map.hasLayer(darkTileLayer)) {
            map.removeLayer(darkTileLayer);
        }
        if (!map.hasLayer(lightTileLayer)) {
            lightTileLayer.addTo(map);
        }
    } else {
        if (map.hasLayer(lightTileLayer)) {
            map.removeLayer(lightTileLayer);
        }
        if (!map.hasLayer(darkTileLayer)) {
            darkTileLayer.addTo(map);
        }
    }
}

/**
 * Update marker styles when station changes
 * @param {string} selectedId - Newly selected station ID
 */
export function updateMarkerStyles(selectedId) {
    const stations = AppState.currentArea?.stations || {};

    Object.entries(stationMarkers).forEach(([id, marker]) => {
        const isSelected = id === selectedId;
        const icon = L.divIcon({
            className: `station-marker ${isSelected ? 'selected' : ''}`,
            html: '<div class="marker-pin"></div>',
            iconSize: [24, 24],
            iconAnchor: [12, 12]
        });
        marker.setIcon(icon);

        const station = stations[id];
        if (station) {
            const localName = station.localName ? `<br><small>${station.localName}</small>` : '';
            marker.setPopupContent(`
                <strong>${station.name}</strong>${localName}
                <button class="popup-btn" onclick="switchStation('${id}')">
                    ${isSelected ? 'Selected' : 'Select'}
                </button>
            `);
        }
    });
}

/**
 * Switch to a different station within the current area
 * In spot mode, delegates to switchSpot with the first spot for that station
 * (popup buttons keep calling switchStation; this keeps one entry point).
 * @param {string} stationId - Station ID to switch to
 */
export async function switchStation(stationId) {
    // Spot mode: map popups navigate by spot, not station
    if (isSpotMode()) {
        if (map) map.closePopup();

        const station = AppState.currentArea?.stations?.[stationId];
        if (station) {
            analytics.mapStationClick(station.name, stationId);
        }

        const spot = await getSpotByStationId(stationId);
        if (spot && window.switchSpot) {
            // switchSpot handles marker refresh via updateMapForRegion
            await window.switchSpot(spot.slug);
        } else {
            logger.warn(`No spot found for station ${stationId}`);
        }
        return;
    }

    if (stationId === AppState.currentStationId) {
        if (map) map.closePopup();
        return;
    }

    // Track map station click
    const station = AppState.currentArea?.stations?.[stationId];
    if (station) {
        analytics.mapStationClick(station.name, stationId);
    }

    // Update state
    setCurrentStation(stationId);

    // Update URL with 3-level hierarchy
    updateUrl(getCurrentRegionId(), getCurrentAreaId(), stationId);

    // Update header location button text
    updateLocationButtonText();

    // Update page title
    if (AppState.currentRegion && AppState.currentStation) {
        updatePageTitle(AppState.currentRegion, AppState.currentArea, AppState.currentStation);
    }

    if (map) {
        map.closePopup();
        updateMarkerStyles(stationId);
    }

    if (refreshAllDataFn) {
        await refreshAllDataFn();
    }
}

/**
 * Focus the map on a specific station
 * @param {string} stationId - Station ID to focus on
 */
export function focusStation(stationId) {
    const station = AppState.currentArea?.stations?.[stationId];
    if (station && map) {
        map.setView([station.lat, station.lon], 12, { animate: true });
    }
}

// Expose switchStation to window for popup onclick handlers
window.switchStation = switchStation;
